import { useEffect, useMemo, useState } from 'react';

import { IContract, IProject, useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { ProjectTreeNode } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';

interface Props {
    projectId: number | undefined;
    contractId: number | undefined;
}

/**
 * This hook manages the state for project and contract selection.
 * If a projectId and contractId are provided (e.g., from an existing widget),
 * it looks them up in the projectHierarchyList and returns the full objects.
 * If not provided (i.e., in create/new mode), it falls back to values from the global store.
 */
export const useProjectContractSelection = ({ projectId, contractId }: Props) => {
    const { contract: contractFromStore, project: projectFromStore } = useProjectTreeStore();
    const { data: projectHierarchyList } = useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');

    const foundProjectFromHierarchyList = useMemo(
        () => projectHierarchyList?.children.find((project) => project.id === projectId),
        [projectHierarchyList],
    );

    const foundContractFromHierarchyList = useMemo(
        () => foundProjectFromHierarchyList?.children.find((contract) => contract.id === contractId),
        [foundProjectFromHierarchyList],
    );

    const initialProject = useMemo(() => {
        if (projectId) {
            if (!foundProjectFromHierarchyList) return null;

            const mappedProject: IProject = {
                id: foundProjectFromHierarchyList?.id,
                title: foundProjectFromHierarchyList?.name,
                uuid: foundProjectFromHierarchyList?.uuid as ProjectTreeNode['uuid'],
            };

            return mappedProject;
        }
        return projectFromStore;
    }, [projectId, foundProjectFromHierarchyList, projectFromStore]);

    const initialContract = useMemo(() => {
        if (contractId) {
            if (!foundContractFromHierarchyList) return null;

            const mappedProject: IProject = {
                id: foundContractFromHierarchyList?.id,
                title: foundContractFromHierarchyList?.name,
                uuid: foundContractFromHierarchyList?.uuid as ProjectTreeNode['uuid'],
            };

            return mappedProject;
        }
        return contractFromStore;
    }, [contractId, foundContractFromHierarchyList, contractFromStore]);

    const [selectedProject, setSelectedProject] = useState<IProject | null>(initialProject ?? null);
    const [selectedContract, setSelectedContract] = useState<IContract | null>(initialContract ?? null);

    const onSelectionFinish = ({ project, contract }: { project: IProject; contract: IContract }) => {
        setSelectedProject(project);
        setSelectedContract(contract);
    };

    useEffect(() => {
        // update state if projectId or contractId or hierarchy list changes
        setSelectedProject(initialProject ?? null);
        setSelectedContract(initialContract ?? null);
    }, [initialProject, initialContract]);

    return {
        selectedProject,
        selectedContract,
        onSelectionFinish,
        projectHierarchyList,
    };
};
